import { Box, TextField } from '@mui/material';
import { isNull, map } from 'lodash';
import moment from 'moment';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { blockedCustomersApi, shopsApi } from 'api';
import {
  CustomersFormSection,
  EntityDetailsPage,
  FormControls,
  FormikNumericField,
  ImportListDialog,
  ShopSelect,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import {
  CUSTOMER_FIELDS,
  useCustomersForm,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { BlockedCustomer } from 'types';
import { formatUtils } from 'utils';

type CustomerItem = {
  id?: string;
  customerId?: string;
  customerIp?: string;
};

type FormValues = {
  shopId: string;
  untilAt?: string;
  duration: number;
  customers: CustomerItem[];
};

const FIELDS = {
  SHOP_ID: 'shopId' as const,
  UNTIL_AT: 'untilAt' as const,
  DURATION: 'duration' as const,
  CUSTOMERS: 'customers' as const,
};

const DEFAULT_CUSTOMER: CustomerItem = { customerId: '', customerIp: '' };

export const BlockedCustomerDetailsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.blocked_customers',
  });
  const { t: tCommon } = useTranslation();
  const { role, isAdmin, isTechOperator } = useUser();
  const { id } = useParams();

  const [importField, setImportField] = useState<
    (typeof CUSTOMER_FIELDS)[keyof typeof CUSTOMER_FIELDS] | null
  >(null);

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.BLOCKED_CUSTOMERS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.BLOCKED_CUSTOMERS;
    }
  }, [isAdmin, isTechOperator]);

  const initialValues: FormValues = useMemo(
    () => ({
      shopId: '',
      untilAt: undefined,
      duration: 0,
      customers: [],
    }),
    [],
  );

  const initialValuesSelect = useCallback(
    (item: BlockedCustomer): FormValues => ({
      shopId: item.shopId,
      untilAt: item.untilAt,
      duration: item.duration,
      customers:
        map(item.customers, ({ id, customerId, customerIp }) => ({
          id,
          customerId,
          customerIp,
        })) ?? [],
    }),
    [],
  );

  const {
    customersSchema,
    renderCustomerItem,
    handleImportIdConfirm,
    handleImportIpConfirm,
    handleError,
  } = useCustomersForm({
    allowToSetIP: true,
    onClose: () => setImportField(null),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        shopId: Yup.string().required(tCommon('errors.required')),
        duration: Yup.number()
          .min(0, tCommon('errors.natural_or_zero_number'))
          .max(60, `${tCommon('errors.max')} 60`)
          .required(tCommon('errors.required')),
        customers: customersSchema,
      }),
    [customersSchema, tCommon],
  );

  const handleBeforeUpdateMutation = useCallback(
    ({ id, data }: { id: string; data: FormValues }) => {
      const { shopId: _, ...rest } = data;
      return { id, data: rest };
    },
    [],
  );

  return (
    <EntityDetailsPage
      id={id!}
      api={blockedCustomersApi}
      apiAsRole={false}
      listUrl={listUrl}
      title={t(isNew ? 'add.title' : 'edit.title')}
      initialValues={initialValues}
      initialValuesSelect={initialValuesSelect}
      queryKey={QueryKey.BlockedCustomers}
      validationSchema={validationSchema}
      onBeforeUpdateMutation={handleBeforeUpdateMutation}
      onError={handleError}
      formClassName="tw-max-w-xl"
    >
      {({ formik }) => (
        <Fragment>
          <FormControls>
            <ShopSelect
              name={FIELDS.SHOP_ID}
              shops={queryResultShops.data}
              required
              disabled={!isNew}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormikNumericField
                name={FIELDS.DURATION}
                label={t('fields.duration')}
                suffix={tCommon('suffixes.minutes')}
                helperText={t('fields.duration_hint')}
                required
                fullWidth
                decimalScale={0}
                allowNegative={false}
                onChangeHandler={(value) =>
                  formik.setFieldValue(
                    FIELDS.UNTIL_AT,
                    value > 0
                      ? moment().add(value, 'minutes').toISOString()
                      : undefined,
                  )
                }
              />
              {!!formik.values.duration && (
                <TextField
                  label={t('fields.until_at')}
                  value={
                    formik.values.untilAt
                      ? formatUtils.formatDate(formik.values.untilAt)
                      : ''
                  }
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                  size="small"
                />
              )}
            </Box>
            <CustomersFormSection
              name={FIELDS.CUSTOMERS}
              label={t('fields.customers')}
              addItemLabel={t('buttons.add_customer')}
              defaultValue={DEFAULT_CUSTOMER}
              ItemComponent={renderCustomerItem}
              importButtons={[
                {
                  label: t('buttons.import_id'),
                  onClick: () => setImportField(CUSTOMER_FIELDS.ID),
                },
                {
                  label: t('buttons.import_ip'),
                  onClick: () => setImportField(CUSTOMER_FIELDS.IP),
                },
              ]}
            />
          </FormControls>
          <ImportListDialog
            open={!isNull(importField)}
            inputLabel={
              importField === CUSTOMER_FIELDS.IP ? t('fields.ip') : undefined
            }
            onClose={() => setImportField(null)}
            onConfirm={(values) =>
              (importField === CUSTOMER_FIELDS.IP
                ? handleImportIpConfirm
                : handleImportIdConfirm)(
                formik.setFieldValue,
                formik.setFieldTouched,
                formik.values.customers,
                values,
              )
            }
          />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
